import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getBlogPosts } from '../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Calendar, Clock, ArrowRight, BookOpen } from 'lucide-react';

const BlogSection = () => {
  const [blogPosts, setBlogPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const response = await getBlogPosts();
        if (!alive) return;
        setBlogPosts(response.posts || []);
      } catch (error) {
        console.error('Cikkek betöltése sikertelen:', error);
      } finally {
        if (alive) setIsLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  if (isLoading) {
    return (
      <section id="blog" className="py-24 bg-white">
        <div className="container mx-auto px-6 text-center">
          <p className="text-gray-600">Cikkek betöltése…</p>
        </div>
      </section>
    );
  }

  if (blogPosts.length === 0) return null;

  // Single-article layout uses a featured side-by-side card; multi-article fallback uses grid.
  const isFeaturedOnly = blogPosts.length === 1;
  const articleHref = (post) => `/tudastar/${post.slug || post.id}`;

  return (
    <section id="blog" data-testid="blog-section" className="py-24 bg-white">
      <div className="container mx-auto px-6">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <div className="inline-block mb-4 px-4 py-2 bg-yellow-100 rounded-full">
            <span className="text-yellow-700 font-semibold text-sm tracking-wider">TUDÁSTÁR</span>
          </div>
          <h2
            data-testid="blog-heading"
            className="text-4xl md:text-5xl font-bold text-gray-900 mb-4"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            Cikkek
            <span className="text-yellow-600"> & útmutatók</span>
          </h2>
          <p className="text-lg text-gray-700">
            Bizonyítékokon alapuló írások edzésről, táplálkozásról és teljesítményoptimalizálásról.
          </p>
        </div>

        {isFeaturedOnly ? (
          <div className="max-w-5xl mx-auto">
            {blogPosts.map((post) => (
              <Link
                key={post.id}
                to={articleHref(post)}
                data-testid={`blog-post-card-${post.id}`}
                className="group block bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 grid md:grid-cols-2"
              >
                <div className="relative h-64 md:h-full overflow-hidden">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="bg-yellow-600 text-white text-xs font-bold px-3 py-1 rounded-full tracking-wider">
                      {post.category}
                    </span>
                  </div>
                </div>

                <div className="p-8 md:p-10 flex flex-col justify-center">
                  <div className="inline-flex items-center gap-2 text-xs font-bold text-gray-500 tracking-widest uppercase mb-3">
                    <BookOpen size={14} />
                    Kiemelt cikk
                  </div>
                  <h3
                    className="text-2xl md:text-3xl font-bold text-gray-900 group-hover:text-yellow-600 transition-colors leading-tight mb-4"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                  >
                    {post.title}
                  </h3>
                  <p className="text-gray-600 text-base leading-relaxed mb-6">
                    {post.excerpt}
                  </p>
                  <div className="flex items-center gap-5 text-sm text-gray-500 mb-5">
                    <div className="flex items-center gap-2">
                      <Calendar size={15} />
                      <span>
                        {new Date(post.date).toLocaleDateString('hu-HU', { month: 'long', day: 'numeric', year: 'numeric' })}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock size={15} />
                      <span>{post.read_time}</span>
                    </div>
                  </div>
                  <span
                    data-testid={`blog-read-${post.id}`}
                    className="self-start inline-flex items-center bg-gray-900 group-hover:bg-yellow-600 text-white font-semibold transition-all duration-300 rounded-md px-4 py-2 text-sm"
                  >
                    Olvass tovább
                    <ArrowRight className="ml-2" size={16} />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-8">
            {blogPosts.map((post) => (
              <Link
                key={post.id}
                to={articleHref(post)}
                data-testid={`blog-post-card-${post.id}`}
                className="group block"
              >
                <Card className="h-full overflow-hidden cursor-pointer transition-all duration-300 hover:shadow-xl group-hover:border-yellow-300">
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="bg-yellow-600 text-white text-xs font-bold px-3 py-1 rounded-full tracking-wider">
                        {post.category}
                      </span>
                    </div>
                  </div>

                  <CardHeader>
                    <CardTitle className="text-xl font-bold text-gray-900 group-hover:text-yellow-600 transition-colors leading-snug">
                      {post.title}
                    </CardTitle>
                    <CardDescription className="text-gray-600 mt-2">
                      {post.excerpt}
                    </CardDescription>
                  </CardHeader>

                  <CardContent>
                    <div className="flex items-center justify-between text-sm text-gray-500">
                      <div className="flex items-center gap-2">
                        <Calendar size={16} />
                        <span>{new Date(post.date).toLocaleDateString('hu-HU', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock size={16} />
                        <span>{post.read_time}</span>
                      </div>
                    </div>
                    <Button
                      variant="link"
                      className="mt-4 p-0 h-auto text-yellow-600 group-hover:text-yellow-700 font-semibold"
                    >
                      Olvass tovább →
                    </Button>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default BlogSection;
